/**
 * Utility functions for consistent action button styling across the application
 */

/**
 * Get styles for an action icon button with specified color variants
 * @param colorMain The main color for the button (typically theme.palette.primary.main)
 * @param bgOpacity The opacity for background color (0.08 by default)
 * @param hoverOpacity The opacity for hover background (0.16 by default)
 * @returns Style object for the IconButton component
 */
export const getActionIconButtonStyle = (
  colorMain: string, 
  bgOpacity: number = 0.08, 
  hoverOpacity: number = 0.16
) => ({
  backgroundColor: `${colorMain}${Math.round(bgOpacity * 255).toString(16).padStart(2, '0')}`,
  color: colorMain,
  '&:hover': {
    backgroundColor: `${colorMain}${Math.round(hoverOpacity * 255).toString(16).padStart(2, '0')}`,
  }
});

/**
 * Predefined styles for common action buttons
 */
export const actionButtonStyles = {
  // Primary action button (view, details, etc.)
  primary: getActionIconButtonStyle('rgb(25, 118, 210)'),
  
  // Secondary action button (edit, manage, etc.)
  secondary: getActionIconButtonStyle('rgb(156, 39, 176)'),
  
  // Success action button (approve, complete, etc.)
  success: getActionIconButtonStyle('rgb(46, 125, 50)'),
  
  // Warning action button (caution actions)
  warning: getActionIconButtonStyle('rgb(237, 108, 2)'),
  
  // Danger action button (delete, remove, etc.)
  danger: getActionIconButtonStyle('rgb(211, 47, 47)'),
};

/**
 * Predefined styles for action buttons with background colors
 * Use these when you want buttons with light background colors
 */
export const actionButtonStylesWithBg = {
  primary: getActionIconButtonStyle('rgb(25, 118, 210)', 0.1),
  secondary: getActionIconButtonStyle('rgb(156, 39, 176)', 0.1),
  success: getActionIconButtonStyle('rgb(46, 125, 50)', 0.1),
  warning: getActionIconButtonStyle('rgb(237, 108, 2)', 0.1),
  danger: getActionIconButtonStyle('rgb(211, 47, 47)', 0.1),
};

/**
 * Style for the container that holds action buttons
 */
export const actionButtonContainerStyle = {
  '& .MuiIconButton-root': {
    transition: 'all 0.2s ease',
    padding: '8px',
    borderRadius: '8px',
  },
  display: 'flex', 
  gap: 1
}; 